import type { ApiResult } from '../types';
import type { RightToInformationWizardDraft } from '../types/rightToInformationCaseRequest';
import type { RightToInformationStepsResponse, RightToInformationSaveResponse, RightToInformationExtensionRequestResponse, RightToInformationExtensionRequestResult } from '../types/rightToInformationApi';
import type { DocumentChecklistItem, LookupItem } from '../mocks/criminalMasterData';
import {
  isRightToInformationCategory,
  setRightToInformationCategoryId,
  FILE_CATEGORY_RIGHT_TO_INFORMATION,
  type RightToInformationWizardStep,
} from '../pages/wizard/rightToInformation/rightToInformationConstants';
import { isDocumentTypeUuid } from '../utils/documentTypeId';
import { DEFAULT_RIGHT_TO_INFORMATION_DRAFT } from '../layouts/RightToInformationRequestLayout';
import { caseApiRequest } from './apiClient';
import {
  caseRequestStepsPath,
  type CaseRequestViewOptions,
} from './caseRequestViewOptions';
import * as criminalService from './criminalCaseRequests';
import {
  buildSaveRightToInformationPayload,
  caseRequestIdFromResponse,
  mapRightToInformationStepsToDraft,
  referenceNoFromResponse,
  resolveRightToInformationDraftResumeStep,
} from './rightToInformationCaseMapper';

export const getFileCategories = criminalService.getFileCategories;
export const getSubFileCategories = criminalService.getSubFileCategories;
export const getSubCategoryApplicableAreas = criminalService.getSubCategoryApplicableAreas;
export const getUrgencyLevels = criminalService.getUrgencyLevels;
export const uploadCaseFile = criminalService.uploadCaseFile;
export const downloadCaseFile = criminalService.downloadCaseFile;
export {
  getRightToInformationCoverNote,
  downloadRightToInformationCoverNotePdf,
} from '../pages/wizard/rightToInformation/rightToInformationCoverNote';

/** Resolve RTI category id from master data when available. */
export async function resolveRightToInformationCategoryId(): Promise<string> {
  const res = await getFileCategories();
  if (!res.success) return '';
  const match = res.data.find((c) => isRightToInformationCategory(c));
  if (match?.id) setRightToInformationCategoryId(match.id);
  return match?.id ?? '';
}

function uuidLookupRows(items: LookupItem[]): LookupItem[] {
  return items.filter((row) => isDocumentTypeUuid(row.id));
}

/** CMS-MMOA-1 — preferred method of access options for RTI step 1 */
export async function getPreferredMethodOfAccess(): Promise<ApiResult<LookupItem[]>> {
  const res = await criminalService.getMethodsOfAccess();
  if (!res.success) return res;
  const data = uuidLookupRows(res.data);
  if (!data.length) return { success: false, message: 'Preferred method of access lookup is unavailable.' };
  return { success: true, data };
}

/** CMS-ML-1 — preferred language options for RTI step 1 */
export async function getPreferredLanguages(): Promise<ApiResult<LookupItem[]>> {
  const res = await criminalService.getLanguages();
  if (!res.success) return res;
  const data = uuidLookupRows(res.data);
  if (!data.length) return { success: false, message: 'Preferred language lookup is unavailable.' };
  return { success: true, data };
}

export async function getDocumentChecklist(
  fileCategoryId: string,
  subFileCategoryId: string,
): Promise<ApiResult<DocumentChecklistItem[]>> {
  if (!fileCategoryId) return { success: true, data: [] };
  return criminalService.getDocumentChecklist(fileCategoryId, subFileCategoryId);
}

export type SaveRightToInformationResult = {
  requestId: string;
  fileReferenceNumber?: string;
};

/** CMS-CRRTI-1 — save/update right to information steps 1–4 */
export async function saveRightToInformationRequest(
  draft: RightToInformationWizardDraft,
  options: {
    isDraft: boolean;
    currentStep?: RightToInformationWizardStep;
    allSteps?: boolean;
    confirmedCorrect?: boolean;
    isUpdateAlreadySubmitted?: boolean;
  },
): Promise<ApiResult<SaveRightToInformationResult>> {
  let fileCategoryId = draft.fileCategoryId.trim();
  if (!fileCategoryId) {
    fileCategoryId = await resolveRightToInformationCategoryId();
  }
  const body = buildSaveRightToInformationPayload(
    fileCategoryId ? { ...draft, fileCategoryId } : draft,
    {
      isDraft: options.isDraft,
      currentStep: options.currentStep,
      allSteps: options.allSteps,
      confirmedCorrect: options.confirmedCorrect,
      isUpdateAlreadySubmitted: options.isUpdateAlreadySubmitted,
    },
  );

  const res = await caseApiRequest<RightToInformationSaveResponse>('/right-to-information/steps/save', {
    method: 'POST',
    body,
  });

  if (!res.ok) return { success: false, message: res.message };

  const requestId = caseRequestIdFromResponse(res.data) || draft.draftRequestId;
  const ref = res.data.reference_no ?? res.data.referenceNo;

  return {
    success: true,
    data: {
      requestId,
      fileReferenceNumber: typeof ref === 'string' && ref.trim() ? ref.trim() : undefined,
    },
  };
}

export type RightToInformationRequestViewData = {
  draft: RightToInformationWizardDraft;
  statusName: string;
  submittedAt: string;
  completionRemarks?: string;
  step1?: RightToInformationStepsResponse['step_1'];
  step2?: RightToInformationStepsResponse['step_2'];
  step3?: RightToInformationStepsResponse['step_3'];
  step4?: RightToInformationStepsResponse['step_4'];
};

/** CMS-CRRTI-2 — full GET payload (wizard resume + view) */
export async function getRightToInformationRequestViewData(
  caseRequestId: string,
  options?: CaseRequestViewOptions,
): Promise<ApiResult<RightToInformationRequestViewData | null>> {
  if (!caseRequestId.trim()) return { success: true, data: null };

  const res = await caseApiRequest<RightToInformationStepsResponse>(
    caseRequestStepsPath(
      `/right-to-information/steps/${encodeURIComponent(caseRequestId)}`,
      options,
    ),
    {
      ...(options?.signal ? { signal: options.signal } : {}),
    },
  );
  if (!res.ok) return { success: false, message: res.message };

  const categoryId = await resolveRightToInformationCategoryId();
  const draft = mapRightToInformationStepsToDraft(res.data, {
    ...DEFAULT_RIGHT_TO_INFORMATION_DRAFT,
    draftRequestId: caseRequestIdFromResponse(res.data) || caseRequestId,
    fileCategoryId: categoryId,
    fileCategory: FILE_CATEGORY_RIGHT_TO_INFORMATION,
  });

  const statusName = (res.data.status_name ?? res.data.status_code ?? '').trim();
  const submittedAt = (res.data.created_at ?? res.data.updated_at ?? res.data.updatedAt ?? '').trim();
  const raw = res.data as { completion_remarks?: string };

  return {
    success: true,
    data: {
      draft,
      statusName,
      submittedAt,
      completionRemarks: typeof raw.completion_remarks === 'string' ? raw.completion_remarks : undefined,
      step1: res.data.step_1,
      step2: res.data.step_2,
      step3: res.data.step_3,
      step4: res.data.step_4,
    },
  };
}

export async function getRightToInformationRequestDetails(
  caseRequestId: string,
): Promise<ApiResult<RightToInformationWizardDraft | null>> {
  const view = await getRightToInformationRequestViewData(caseRequestId);
  if (!view.success) return { success: false, message: view.message };
  return { success: true, data: view.data?.draft ?? null };
}

export async function getRightToInformationDraftResumeStep(
  caseRequestId: string,
): Promise<ApiResult<{ step: RightToInformationWizardStep; requestId: string }>> {
  if (!caseRequestId.trim()) return { success: false, message: 'Case request id is missing.' };

  const res = await caseApiRequest<RightToInformationStepsResponse>(
    `/right-to-information/steps/${encodeURIComponent(caseRequestId)}`,
  );
  if (!res.ok) return { success: false, message: res.message };

  const requestId = caseRequestIdFromResponse(res.data) || caseRequestId;
  return {
    success: true,
    data: { step: resolveRightToInformationDraftResumeStep(res.data), requestId },
  };
}

export async function submitRightToInformationRequest(
  draft: RightToInformationWizardDraft,
): Promise<ApiResult<SaveRightToInformationResult>> {
  return saveRightToInformationRequest(draft, {
    isDraft: false,
    allSteps: true,
    confirmedCorrect: draft.confirmCorrect,
  });
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function strField(raw: Record<string, unknown>, ...keys: string[]): string {
  for (const key of keys) {
    const v = raw[key];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return '';
}

function normalizeExtensionRequestResult(raw: unknown): RightToInformationExtensionRequestResult {
  const r = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  return {
    caseRequestId: strField(r, 'case_request_id', 'caseRequestId'),
    workflowInstanceStageUserTaskId: strField(
      r,
      'workflow_instance_stage_user_task_id',
      'workflowInstanceStageUserTaskId',
    ),
    onHold: r.on_hold === true || r.onHold === true,
    caseRequestStatusCode: strField(r, 'case_request_status_code', 'caseRequestStatusCode'),
    caseRequestStatusName: strField(r, 'case_request_status_name', 'caseRequestStatusName'),
    communicationId: strField(r, 'communication_id', 'communicationId'),
    auditLogId: strField(r, 'audit_log_id', 'auditLogId'),
  };
}

/** CMS-CRRTI-3 — request an RTI case extension (places case on hold). */
export async function requestRightToInformationExtension(input: {
  caseRequestId: string;
  workflowInstanceStageUserTaskId: string;
  description: string;
}): Promise<ApiResult<RightToInformationExtensionRequestResult>> {
  const caseRequestId = input.caseRequestId.trim();
  const workflowInstanceStageUserTaskId = input.workflowInstanceStageUserTaskId.trim();
  const description = input.description.trim();

  if (!UUID_RE.test(caseRequestId)) {
    return { success: false, message: 'Invalid case request id.' };
  }
  if (!UUID_RE.test(workflowInstanceStageUserTaskId)) {
    return { success: false, message: 'Invalid workflow task id.' };
  }
  if (!description) {
    return { success: false, message: 'Reason for requesting an extension is required.' };
  }
  if (description.length > 2000) {
    return { success: false, message: 'Reason must be 2000 characters or fewer.' };
  }

  const res = await caseApiRequest<RightToInformationExtensionRequestResponse>(
    '/right-to-information/extension-request',
    {
      method: 'POST',
      body: {
        case_request_id: caseRequestId,
        workflow_instance_stage_user_task_id: workflowInstanceStageUserTaskId,
        description,
      },
    },
  );

  if (!res.ok) return { success: false, message: res.message };

  return { success: true, data: normalizeExtensionRequestResult(res.data) };
}

export type { DocumentChecklistItem };
